import type DestinationsCard from "@travily/types/models/DestinationsCard";
import type { DestinationsCardPhotoTrait } from "@travily/types/models/DestinationsCard";
import type DestinationSearchHit from "@travily/types/pending/models/DestinationSearchHit";
import { localizedCountryName } from "@ui/utils/country-name";
import { DestinationCard } from "@/components/DestinationCard/DestinationCard";
import {
  resolveDestinationPrice,
  resolveDestinationTemperature,
} from "@/components/DestinationCard/resolve-destination";

// Mean of the destination's average temperature across the travel window, so the
// card's number matches the window-averaged ranking. Months with no data are
// skipped; null when none of the window's months have data.
function getWindowAvgTemp(hit: DestinationSearchHit, months: number[]): number | null {
  let sum = 0;
  let present = 0;
  for (const month of months) {
    const value = hit[`avg_temp_${month}` as keyof DestinationSearchHit];
    if (typeof value === "number") {
      sum += value;
      present += 1;
    }
  }
  return present > 0 ? sum / present : null;
}

export interface DestinationHitCardProps {
  /** Travel window months; the card temperature shows their average (matches ranking). */
  effectiveMonths?: number[];
  favorited?: boolean;
  hit: DestinationSearchHit;
  onFavoriteToggle?: () => void;
  /** Fires when the card is opened, before navigation. */
  onPress?: () => void;
  rank?: number;
}

export function DestinationHitCard({
  effectiveMonths,
  favorited,
  hit,
  onFavoriteToggle,
  onPress,
  rank,
}: DestinationHitCardProps) {
  const avgTemp = effectiveMonths?.length ? getWindowAvgTemp(hit, effectiveMonths) : null;
  const { temperature, weatherIcon } = resolveDestinationTemperature(avgTemp);

  return (
    <DestinationCard
      destination={hitToDestination(hit)}
      favorited={favorited ?? false}
      onFavoriteToggle={onFavoriteToggle}
      onPress={onPress}
      price={resolveDestinationPrice(hit)}
      priceGrade={hit.cost_evidence_grade}
      rank={rank}
      rankExplanation={hit._travily_ranking}
      showDescription
      temperature={temperature}
      theme="compact"
      weatherIcon={weatherIcon}
    />
  );
}

function hitToDestination(hit: DestinationSearchHit): DestinationsCard & DestinationsCardPhotoTrait {
  return {
    id: hit.id,
    name: hit.name,
    slug: hit.slug,
    country_slug: hit.country_slug,
    country_name: localizedCountryName(hit.country_code),
    marketing_sentence: hit.marketing_sentence,
    photo: hit.cover_image,
  };
}
